import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 타임아웃 설정 (3초 후 자동으로 로딩 해제)
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn('인증 확인 타임아웃 - 로딩 해제');
        setLoading(false);
      }
    }, 3000);

    // 현재 세션 확인
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setUser(session?.user ?? null);
        setLoading(false);
        clearTimeout(timeout);
      })
      .catch((error) => {
        console.error('세션 확인 오류:', error);
        setUser(null);
        setLoading(false);
        clearTimeout(timeout);
      });

    // 인증 상태 변화 감지
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        
        // 회원가입 시에만 프로필 생성
        if (event === 'SIGNED_UP' && session?.user) {
          await ensureProfile(session.user);
        }
        
        setLoading(false);
      }
    );

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  // 프로필 존재 확인 및 생성 (회원가입 시에만 사용)
  const ensureProfile = async (user: User) => {
    try {
      // upsert를 사용하여 한 번의 쿼리로 처리
      const { error } = await supabase
        .from('profiles')
        .upsert([
          {
            id: user.id,
            email: user.email,
            name: user.user_metadata?.name || '',
            phone: user.user_metadata?.phone || '',
            role: 'user'
          }
        ], {
          onConflict: 'id',
          ignoreDuplicates: true
        });

      if (error && error.code !== '23505') {
        console.error('프로필 생성 오류:', error);
      }
    } catch (error) {
      console.error('프로필 확인 오류:', error);
    }
  };

  // 회원가입
  const signUp = async (email: string, password: string, name: string, phone: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            phone,
          },
          emailRedirectTo: undefined, // 이메일 리다이렉트 비활성화
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('회원가입 오류:', error);
      throw error;
    }
  };

  // 로그인 (최적화됨)
  const signIn = async (email: string, password: string) => {
    try {
      // 입력값 검증
      if (!email || !password) {
        throw new Error('이메일과 비밀번호를 모두 입력해주세요.');
      }

      if (!email.includes('@')) {
        throw new Error('올바른 이메일 형식을 입력해주세요.');
      }

      if (password.length < 6) {
        throw new Error('비밀번호는 최소 6자 이상이어야 합니다.');
      }

      // 이메일 정규화 (공백 제거 및 소문자 변환)
      const cleanEmail = email.trim().toLowerCase();

      const { data, error } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password: password.trim(),
      });

      if (error) {
        // Supabase 오류를 사용자 친화적인 메시지로 변환
        if (error.message === 'Invalid login credentials' || error.message.includes('Invalid')) {
          throw new Error('이메일 또는 비밀번호가 일치하지 않습니다.');
        } else if (error.message.includes('Email not confirmed')) {
          throw new Error('이메일 인증이 완료되지 않았습니다.');
        } else if (error.message.includes('Too many requests')) {
          throw new Error('너무 많은 로그인 시도가 있었습니다. 잠시 후 다시 시도해주세요.');
        } else if (error.message.includes('fetch') || error.message.includes('network')) {
          throw new Error('네트워크 연결을 확인해주세요.');
        } else {
          throw new Error('로그인 중 오류가 발생했습니다.');
        }
      }

      if (!data.user) {
        throw new Error('로그인에 실패했습니다. 다시 시도해주세요.');
      }

      return data;
    } catch (error: any) {
      // 오류를 다시 던져서 상위 컴포넌트에서 처리
      throw error;
    }
  };

  // 비밀번호 재설정
  const resetPassword = async (email: string) => {
    try {
      if (!email || !email.includes('@')) {
        throw new Error('올바른 이메일 주소를 입력해주세요.');
      }

      const cleanEmail = email.trim().toLowerCase();
      
      // 현재 URL의 origin을 사용
      const redirectUrl = `${window.location.origin}/reset-password`;

      const { error } = await supabase.auth.resetPasswordForEmail(
        cleanEmail,
        {
          redirectTo: redirectUrl,
        }
      );

      if (error) {
        console.error('Supabase 비밀번호 재설정 오류:', error);
        
        // 네트워크 오류 처리
        if (error.message.includes('fetch') || error.message.includes('network')) {
          throw new Error('네트워크 연결을 확인해주세요. Supabase 서비스에 접속할 수 없습니다.');
        }
        
        // 이메일 설정 오류 처리
        if (error.message.includes('email') || error.message.includes('SMTP')) {
          throw new Error('이메일 발송 설정에 문제가 있습니다. 관리자에게 문의해주세요.');
        }
        
        if (error.message.includes('User not found')) {
          throw new Error('해당 이메일로 가입된 계정이 없습니다.');
        }
        
        throw new Error(`비밀번호 재설정 오류: ${error.message}`);
      }

      return true;
    } catch (error: any) {
      console.error('비밀번호 재설정 오류:', error);
      throw error;
    }
  };

  // 계정 찾기 (이름과 연락처로 이메일 찾기)
  const findAccount = async (name: string, phone: string) => {
    try {
      if (!name || !phone) {
        throw new Error('이름과 연락처를 모두 입력해주세요.');
      }

      const cleanName = name.trim();
      const cleanPhone = phone.trim();

      // profiles 테이블에서 이름과 연락처로 검색
      const { data, error } = await supabase
        .from('profiles')
        .select('email')
        .eq('name', cleanName)
        .eq('phone', cleanPhone);

      if (error) {
        console.error('Supabase 계정 찾기 오류:', error);
        
        // 네트워크 오류 처리 (가장 먼저 체크)
        if (error.message.includes('fetch') || error.message.includes('Failed to fetch') || error.message.includes('network')) {
          throw new Error('네트워크 연결을 확인해주세요.\n\nSupabase 서비스에 접속할 수 없습니다.\n관리자에게 문의해주세요.');
        }
        
        // 익명 로그인 비활성화 오류 처리
        if (error.message.includes('Anonymous sign-ins are disabled') || error.message.includes('anonymous')) {
          throw new Error('계정 찾기 기능을 사용하려면 Supabase 설정이 필요합니다.\n\nSupabase 대시보드 → Authentication → Providers → Anonymous Sign-ins를 활성화해주세요.');
        }
        
        // 권한 오류 처리
        if (error.code === '42501' || error.message.includes('permission') || error.message.includes('denied')) {
          throw new Error('데이터베이스 접근 권한이 없습니다.\n\n관리자에게 문의해주세요.');
        }
        
        // 기타 오류
        throw new Error(`계정 찾기 중 오류가 발생했습니다.\n\n${error.message}\n\n관리자에게 문의해주세요.`);
      }

      // 데이터가 없거나 빈 배열인 경우
      if (!data || data.length === 0) {
        return null;
      }

      return data[0]?.email || null;
    } catch (error: any) {
      console.error('계정 찾기 오류:', error);
      throw error;
    }
  };

  // 로그아웃
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return {
    user,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    findAccount,
  };
}
